import { ArrowUpRight, Facebook, Instagram, Youtube } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { SITE } from "../config/site";
import { track } from "../lib/analytics";

const SubstackIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
  </svg>
);

const PLATFORMS = [
  {
    id: "instagram",
    icon: Instagram,
    name: "Instagram",
    handle: "@official.rootedhealing",
    blurb: "Daily notes on natural living, motherhood, and small rooted rituals.",
  },
  {
    id: "substack",
    icon: SubstackIcon,
    name: "Substack",
    handle: "Staci's writing",
    blurb: "Longer reflections and essays on wellness, faith, and conscious family life.",
  },
  {
    id: "facebook",
    icon: Facebook,
    name: "Facebook",
    handle: "Community page",
    blurb: "Gather with the community, conversations, and updates as they grow.",
  },
  {
    id: "youtube",
    icon: Youtube,
    name: "YouTube",
    handle: "Video teachings",
    blurb: "Educational videos and gentle teachings. A growing library to come.",
  },
];

export const Community = () => (
  <section id="community" data-testid="community-resources-section" className="bg-forest py-24 text-ivory sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Overline light>Resources & Community</Overline>
        <h2 className="font-serif text-3xl leading-tight text-ivory sm:text-4xl lg:text-5xl">
          Learn with Staci, <em className="italic text-gold-soft">wherever you are</em>
        </h2>
        <p className="mt-6 text-lg font-light leading-relaxed text-ivory/70">
          Educational writing, video teachings, and everyday encouragement live across Staci's
          platforms. This corner will soon feature selected videos, articles, and downloadable
          resources.
        </p>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {PLATFORMS.map((p, i) => {
          const url = SITE.social[p.id];
          const inner = (
            <>
              <div className="flex items-center justify-between">
                <p.icon className="h-6 w-6 text-gold-soft" />
                {url && (
                  <ArrowUpRight className="h-4 w-4 text-ivory/50 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-gold-soft" />
                )}
              </div>
              <h3 className="mt-8 font-serif text-xl text-ivory">{p.name}</h3>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-gold-soft/80">{p.handle}</p>
              <p className="mt-4 text-sm leading-relaxed text-ivory/65">{p.blurb}</p>
              {!url && (
                <span className="mt-5 inline-flex w-fit rounded-full border border-ivory/25 px-3.5 py-1 text-[11px] uppercase tracking-[0.18em] text-ivory/60">
                  Link coming soon
                </span>
              )}
            </>
          );
          const cls =
            "group flex h-full flex-col rounded-[1.75rem] border border-ivory/15 bg-ivory/[0.04] p-8 transition-all duration-500";
          return (
            <Reveal key={p.id} delay={i * 0.08}>
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-link-${p.id}`}
                  onClick={() => track("social_click", { platform: p.id })}
                  className={`${cls} hover:-translate-y-1.5 hover:border-gold-soft/40 hover:bg-ivory/[0.08]`}
                >
                  {inner}
                </a>
              ) : (
                <div data-testid={`social-card-${p.id}`} className={cls}>
                  {inner}
                </div>
              )}
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.2}>
        <p className="mt-12 max-w-2xl text-sm leading-relaxed text-ivory/55">
          Platforms come and go. Staci's email community is where the conversation always
          continues. Join below and never miss a letter.
        </p>
      </Reveal>
    </div>
  </section>
);
